import PropTypes from 'prop-types';
import { useState, useEffect, memo } from 'react';
import { Svg } from '../Svg.js';
import { formatPhoneNumber, isValidPhoneNumber } from '../../utils/addressUtils.ts';

// general.js
/* global dcAnalytics */

export const displaySignupWidget = function (user, dismissible) {
    return user && user.optedIntoAnyTexts === false &&
            (!dismissible || !user.smsPromptDismissed);
};

const _SmsSignupWidget = function ({ user, dismissible, optInUpdateMethod, submitFunctionRef }) {
    const [submitted, setSubmitted] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    const [optedIntoTransactionalTexts, setOptedIntoTransactionalTexts] = useState(false);
    const [optedIntoMarketingTexts, setOptedIntoMarketingTexts] = useState(false);
    const [trackedView, setTrackedView] = useState(false);
    const [phone, setPhone] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        // We want to track this a single time,
        // once the user is fully signed in and able to see it
        if (!trackedView && displaySignupWidget(user, dismissible)) {
            dcAnalytics.track('SMS Signup Widget', {
                userId: user.userId,
                optInUpdateMethod,
            });
            setTrackedView(true);
        }
    }, [dismissible, optInUpdateMethod, trackedView, user]);

    const onChange = function (event) {
        setPhone(formatPhoneNumber(event.target.value));
    };

    const onOptedIntoTransactionalTextsChange = function (event) {
        setOptedIntoTransactionalTexts(event.target.value);
    }

    const onOptedIntoMarketingTextsChange = function (event) {
        setOptedIntoMarketingTexts(event.target.value);
    }

    const dismiss = function (event) {
        event.preventDefault();

        $.ajax({
            type: 'post',
            url: '/common/sms-settings.json',
            data: {
                dismiss: true,
            },
        });
        // Assume the server call succeeds
        setDismissed(true);
    };

    const onClick = function (event) {
        event.preventDefault();

        if (!isValidPhoneNumber(phone)) {
            setError('Please include a valid phone number.');
            return;
        }

        submit();
    };

    const submit = function () {
        // We assume success since there isn't much we could do with a failure response
        setSubmitted(true);
        return $.ajax({
            type: 'post',
            url: '/common/sms-settings.json',
            data: {
                phone,
                optedIntoTransactionalTexts: optedIntoTransactionalTexts,
                optedIntoMarketingTexts: optedIntoMarketingTexts,
                optInUpdateMethod: optInUpdateMethod,
            },
        });
    };

    if (submitFunctionRef) {
        submitFunctionRef.current = (callback) => {
            if (!submitted && !dismissed && isValidPhoneNumber(phone)) {
                submit().always(callback);
            } else {
                callback();
            }
        };
    }

    if (dismissed || !displaySignupWidget(user, dismissible)) {
        return null;
    }

    if (submitted) {
        return (
            <div id="sms-finished-prompt" className="alert-message notification">
                <div className="sms-signup-illustration"></div>
                <div className="prompt-body">
                    <h3>Thank you!</h3>
                    <p>
                        You&apos;ll get a text from us to confirm your registration.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div id="sms-loud-prompt">
            {dismissible
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
                ? <a href="" className="close" aria-label="close alert" onClick={dismiss}>
                    <Svg icon="delete" attributes={{ role: 'presentation' }}
                        additionalClasses="presentation" />
                </a>
                : ''
            }
            <div className="sms-cta">
                <input id="unfurl" className="spend-control" name="spend" type="checkbox"/>
                <div className="upsell">
                    <div>
                        <h3>Multiply your next donation!</h3>
                        <p>Signing up for texts is the best way to keep up with future match offers.</p>
                        <label
                        htmlFor="unfurl" className="tab-choice">
                            <span className="button-secondary small toggle heap-first-sms-signup-button" tabIndex="0" role="button" aria-label="">Sign up for texts</span>
                        </label>
                    </div>
                    <div className="sms-flair"></div>
                </div>
                <div className="collapsed">
                    <ul className="benefits">
                        <li><div className="sms-icon double">2x</div><p>Get alerts on the next match day</p></li>
                        <li><div className="sms-icon flash"><Svg icon="flash" attributes={{ role: 'presentation' }} additionalClasses="presentation" /></div><p>Respond to the latest teacher needs</p></li>
                        <li><div className="sms-icon"><Svg icon="banknote" attributes={{ role: 'presentation' }} additionalClasses="presentation" /></div><p>Get promo codes to boost your next donation</p></li>
                        <li><div className="sms-icon x"><Svg icon="delete" attributes={{ role: 'presentation' }} additionalClasses="presentation" /></div><p>Cancel at anytime by replying <strong>STOP</strong></p></li>
                    </ul>
                        <div className="form-redux">
                            <div className="col col12 optedDisclaimer">
                                <div className="input-holder">
                                    <input name="optIntoPromotionalTexts" id="optIntoPromotionalTexts" type="checkbox" onChange={onOptedIntoMarketingTextsChange} defaultChecked={false} />
                                    <label htmlFor="optIntoPromotionalTexts">
                                        {user.userType === 'teacher'
                                            ? <p>By signing up, you agree to receive recurring automated promotional
                                                and personalized marketing text messages (e.g. match offers) from
                                                DonorsChoose
                                                at the cell number used when signing up. Consent is not a condition of any
                                                purchase. Reply <strong>HELP</strong> for help and <strong>STOP</strong> to cancel. Msg frequency varies.
                                                Msg & data rates may apply. View <a href={baseURL + 'terms'} target="_blank" rel="noopener noreferrer">Terms</a> and <a href={baseURL + 'privacy-policy'} target="_blank" rel="noopener noreferrer">Privacy</a>.</p>
                                            : <p className="text-discreet">
                                                By signing up, you agree to receive recurring automated promotional and personalized marketing text messages (e.g. project recommendations) from DonorsChoose at the cell number used when signing up. Consent is not a condition of any purchase. Reply HELP for help and STOP to cancel. Msg frequency varies. Msg & data rates may apply. View <a href={baseURL + 'terms'} target="_blank" rel="noopener noreferrer">Terms</a> and <a href={baseURL + 'privacy-policy'} target="_blank" rel="noopener noreferrer">Privacy</a></p>
                                        }
                                    </label>
                                </div>
                                <div className="input-holder opt-in">
                                    <label htmlFor="optIntoTransactionalTexts">
                                        <input name="optIntoTransactionalTexts" id="optIntoTransactionalTexts" type="checkbox" onChange={onOptedIntoTransactionalTextsChange} defaultChecked={false} />
                                        {user.userType === 'teacher'
                                            ? <p><em>(Optional)</em> Get text alerts about your project.</p>
                                            : <p><em>(Optional)</em> Get text alerts about the projects you support.</p>
                                        }
                                    </label>
                                </div>
                            </div>
                            <div className="error-text">{error}</div>
                            <label>Mobile phone number</label>
                            <input type="text" name="phone" aria-label="Enter your phone number, text box numeric only" placeholder="(000) 000-0000" value={phone} onChange={onChange} />
                            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                            <a className="button-secondary button-overlay small inline-button heap-second-sms-signup-button" href="#" onClick={onClick}>Sign up for texts</a>
                        </div>
                </div>
            </div>
        </div>
    );
};
_SmsSignupWidget.propTypes = {
    user: PropTypes.object,
    dismissible: PropTypes.bool,
    optedIntoTransactionalTexts: PropTypes.bool,
    optedIntoMarketingTexts: PropTypes.bool,
    optInUpdateMethod: PropTypes.string.isRequired,
    submitFunctionRef: PropTypes.object,
};

export const SmsSignupWidget = memo(_SmsSignupWidget);
